<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Attribute'): $t('Edit Attribute')  }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Attribute Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-6">
            <label
              for="name"
              class="form-label"
            >
              {{ $t('Attribute Name (TH)') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="name"
                type="text"
                v-model.trim="v$.form.name.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter attribute name') + '...'"
              >
              <div v-for="error of v$.form.name.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label
              for="name_en"
              class="form-label"
            >
              {{ $t('Attribute Name (EN)') }}
            </label>
            <div class="input-group-sm">
              <input
                id="name_en"
                type="text"
                v-model.trim="v$.form.name_en.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter attribute name in English') + '...'"
              >
              <div class="form-text text-muted">{{ $t('Leave blank to show the Thai name.') }}</div>
              <div v-for="error of v$.form.name_en.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label
              for="code"
              class="form-label"
            >
              {{ $t('Attribute Code') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="code"
                type="text"
                v-model.trim="v$.form.code.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter attribute code') + '...'"
              >
              <div v-for="error of v$.form.code.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-list-check me-2" />
        {{ $t('Options') }}
      </div>
      <div class="px-3">
        <div
          class="overflow-x-auto"
        >
          <table class="table table-sm">
            <thead class="table-light">
              <tr>
                <th
                  style="min-width: 130px;"
                >
                  {{ $t('Option Name (TH)') }}
                </th>
                <th
                  style="min-width: 130px;"
                >
                  {{ $t('Option Name (EN)') }}
                </th>
                <th
                  style="min-width: 100px;"
                >
                  {{ $t('Option Code') }}
                </th>
                <th
                  class="text-end"
                  style="min-width: 100px;max-width: 100px;width: 100px;"
                >
                  {{ $t('Action') }}
                </th>
              </tr>
            </thead>
            <tbody
              class="align-baseline"
            >
              <tr
                v-for="(option, index) in form.options"
              >
                <option-form
                  :index="index"
                  :option="option"
                />
              </tr>
              <tr
                v-if="form.options.length == 0"
              >
                <td
                  colspan="4"
                  class="text-center"
                >
                  {{ $t('No Available Data') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <ladda
          text="Add"
          :btn-class="'btn btn-primary btn-block '"
          icon="<i class='fa-solid fa-plus' /i>"
          @click="addOption"
        />
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Attributes } from '@/services/endpoint/attributes.service';
import OptionForm from './OptionForm.vue';
import numeral from 'numeral';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      form: {
        name: '',
        name_en: '',
        code: '',
        options: []
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    OptionForm,
    AccessDenied
  },
  methods: {
    async deleteItem(index) {
      this.form.options.splice(index, 1);
    },
    addOption() {
      this.form.options.push({
        name: '',
        name_en: '',
        code: ''
      });
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getCreateData() {
      await Attributes.getCreate().then(res => {
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    async getUpdateData() {
      await Attributes.getUpdate(this.id).then(res => {
        let vm = this;
        this.form = {
          name: res.data.data.name,
          name_en: res.data.data.name_en,
          code: res.data.data.code,
          options: res.data.data.options.map(function(option) {
            return {
              id: option.id,
              name: option.name,
              name_en: option.name_en,
              code: option.code
            }
          })
        };
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    setParamsBeforeSendCreate() {
      let params = _.cloneDeep(this.form);
      $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
      // ชื่ออังกฤษเป็น optional — ส่ง null ไปด้วยตอนล้างค่า ไม่งั้น key ถูกตัดทิ้ง
      // ตาม rule "ค่าว่าง = ไม่ส่ง" ด้านบน แล้ว BE จะมองว่าไม่ได้แก้ = ค่าเดิมค้าง
      params.name_en = this.form.name_en || null;
      params.options = (params.options || []).map(option => ({
        ...option,
        name_en: option.name_en || null
      }));
      return params;
    },
    setParamsBeforeSendUpdate() {
      let params = _.cloneDeep(this.form);
      $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
      // ชื่ออังกฤษเป็น optional — ส่ง null ไปด้วยตอนล้างค่า ไม่งั้น key ถูกตัดทิ้ง
      // ตาม rule "ค่าว่าง = ไม่ส่ง" ด้านบน แล้ว BE จะมองว่าไม่ได้แก้ = ค่าเดิมค้าง
      params.name_en = this.form.name_en || null;
      params.options = (params.options || []).map(option => ({
        ...option,
        name_en: option.name_en || null
      }));
      return params;
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await Attributes.update(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Attribute!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      this.loadingSave = false;
      await Attributes.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Attribute!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  },
  computed: {
  }
}
</script>
